import React, { useState, useEffect, useMemo } from 'react';
import { Button, SearchInput, Typography, COLORS } from 'pvs-design-system';
import { Box, Stack } from '@mui/material';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LinksTable from 'components/LinksTable/LinksTable';
import { Link, useLocation } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { PAGE_TITLE_HEADER_TEXT_ID, ROUTES } from 'constants/constants';
import { isFeatureEnabled, FEATURE_FLAGS } from 'utils/featureFlags';
import bootConfig from 'utils/bootConfig';
import { Link as LinkType } from 'types';
import { useBulkExportLinksMutation } from 'api';
import BuildUrl from 'utils/formatLink';
import {
  BulkExportLinkItem,
  ALFRESCO_THREE_PI_VENDOR,
} from 'types/BulkExportLinks';

import { Notify } from 'components/Notifications/Notifications';

/**
 * Pagination & selection (per UX guidelines):
 * - Selection is global: selectedLinks persists across page/sort/pageSize changes.
 * - Selection count reflects all selected links across all pages (for export).
 * - Select All / Deselect All in the table applies only to the current page.
 * - Export uses the full set of selected links from all pages.
 */
const LinksManagementPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [searchValue] = useDebounce(search, 250);
  const [selectedLinks, setSelectedLinks] = useState<Record<string, LinkType>>(
    {}
  );

  const location = useLocation();
  const enableBulkExport = isFeatureEnabled(FEATURE_FLAGS.BULK_LINK_EXPORT);
  const canManageLinks =
    bootConfig.authInfo.permissions.canManagePlatform ||
    bootConfig.authInfo.permissions.canCreateLinks ||
    bootConfig.authInfo.permissions.hasLTI13PaicePermission;

  const [bulkExportLinks, { isLoading: isExporting }] =
    useBulkExportLinksMutation();

  const selectedCount = useMemo(
    () => Object.keys(selectedLinks).length,
    [selectedLinks]
  );

  const [totalLinkCount, setTotalLinkCount] = useState(0);

  useEffect(() => {
    if (location.state?.toastMessage) {
      if (location.state.toastType === 'error') {
        Notify.error(location.state.toastMessage);
      } else {
        Notify.success(location.state.toastMessage);
      }
    }
  }, [location.state]);

  const handleToggleLink = (link: LinkType, selected: boolean) => {
    setSelectedLinks((prev) => {
      const next = { ...prev };
      if (selected) {
        next[link.id] = link;
      } else {
        delete next[link.id];
      }
      return next;
    });
  };

  const handleToggleAllVisible = (links: LinkType[], selected: boolean) => {
    setSelectedLinks((prev) => {
      const next = { ...prev };
      links.forEach((link) => {
        if (selected) {
          next[link.id] = link;
        } else {
          delete next[link.id];
        }
      });
      return next;
    });
  };

  const handleExportLinks = async () => {
    if (selectedCount === 0) {
      return;
    }

    const links: BulkExportLinkItem[] = Object.values(selectedLinks).map(
      (link) => ({
        title: link.name ?? '',
        externalUrlLink: BuildUrl(bootConfig.ltiHandlerServiceBaseUrl, link.id),
        threePIVendor: ALFRESCO_THREE_PI_VENDOR,
      })
    );

    try {
      await bulkExportLinks({ links }).unwrap();

      Notify.success(
        <>
          <strong>Export started.</strong> Processing {selectedCount} link
          {selectedCount === 1 ? '' : 's'}.
        </>
      );

      setSelectedLinks({});
    } catch {
      Notify.error(
        <>
          <strong>Export failed.</strong> Couldn&apos;t generate export file for{' '}
          {selectedCount} link
          {selectedCount === 1 ? '' : 's'}. Please try again.
        </>
      );
    }
  };

  return (
    <>
      <Typography id={PAGE_TITLE_HEADER_TEXT_ID} variant="h1">
        Link Manager
      </Typography>
      <Stack
        direction="row"
        sx={{
          marginTop: '24px',
          marginBottom: '35px',
          alignItems: 'center',
        }}
        spacing={2}
      >
        <Stack flex="1" direction="row">
          <SearchInput
            id="searchInput"
            ariaLabel="Search"
            size="medium"
            sx={{ flex: 1 }}
            placeholder="Search link name, link ID, keyword, or tool name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          {enableBulkExport && (
            <Button
              id="exportLinks"
              color="secondary"
              size="medium"
              variant="contained"
              startIcon={<FileDownloadIcon />}
              disabled={!canManageLinks || selectedCount === 0 || isExporting}
              onClick={handleExportLinks}
            >
              {isExporting ? 'Exporting...' : 'Export Selected (All Pages)'}
            </Button>
          )}
          <Button
            id="importLog"
            color="secondary"
            size="medium"
            component={Link}
            to={ROUTES.importLogs}
            disabled={
              !bootConfig.authInfo.permissions.canManagePlatform &&
              !bootConfig.authInfo.permissions.canCreateLinks &&
              !bootConfig.authInfo.permissions.hasLTI13PaicePermission
            }
          >
            Import Log
          </Button>
          <Button
            id="addNewLink"
            startIcon={<ControlPointIcon />}
            component={Link}
            to={ROUTES.createLink}
            disabled={!canManageLinks}
          >
            Add New Links
          </Button>
        </Stack>
      </Stack>

      {enableBulkExport && selectedCount > 0 && (
        <Box
          sx={{
            backgroundColor: COLORS.purple[700],
            color: COLORS.white,
            padding: '12px 16px',
            marginBottom: '12px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="p-md"
            sx={{ color: COLORS.white, fontWeight: 400 }}
          >
            {totalLinkCount > 0 && selectedCount <= totalLinkCount
              ? `Selected ${selectedCount} of ${totalLinkCount}`
              : `Selected ${selectedCount} links`}
          </Typography>
        </Box>
      )}

      <LinksTable
        searchValue={searchValue}
        selectedLinkIds={Object.keys(selectedLinks)}
        onToggleLink={handleToggleLink}
        onToggleAllVisible={handleToggleAllVisible}
        canSelect={canManageLinks && !isExporting}
        showSelection={enableBulkExport}
        onTotalCountChange={setTotalLinkCount}
      />
    </>
  );
};

export default LinksManagementPage;
